import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { createPortal } from "react-dom";
import JSZip from "jszip";
import exifr from "exifr";
import {
  UsersRound, Download, X, Loader2, Upload,
  ChevronLeft, ChevronRight, CheckCircle2, AlertCircle,
  Archive, Sparkles, FolderOpen,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Concurrency cap so we don't hammer the LLM rate limit with a large
// bulk upload. Requests fire 3-at-a-time; UI updates as each returns.
const MAX_PARALLEL = 3;

// Confidence thresholds for the post-processing pass. Any SFace match
// below AI_TRIGGER is retried via AI + cluster propagation.
const CONFIDENT_SIM = 0.55;
const AI_TRIGGER    = 0.35;
// Files within this many seconds of each other are treated as the
// "same shoot" — likely the same person from multiple exposures.
// Kept tight (3 s) because photographers often move on to the next
// subject within ~5-10 s, so anything wider merges neighbours.
const CLUSTER_WINDOW_SEC = 3;
// Files whose numeric IMG_XXXX suffix differs by at most this are
// treated as clustered even when EXIF is missing. Kept at 1 so that
// only truly consecutive frames merge.
const CLUSTER_NUMBER_GAP = 1;

/** Pull the trailing integer out of a filename ("IMG_0071.JPG" → 71).
    Returns null when there's no numeric suffix. */
function filenameNumber(name) {
  const m = /(\d+)(?=\.[^.]+$)/.exec(name || "");
  return m ? parseInt(m[1], 10) : null;
}

/** Read EXIF DateTimeOriginal as a UNIX timestamp (seconds). Silently
    returns null on files without EXIF. */
async function fileTimestamp(file) {
  try {
    const meta = await exifr.parse(file, ["DateTimeOriginal", "CreateDate"]);
    const d = meta?.DateTimeOriginal || meta?.CreateDate;
    if (d instanceof Date && !isNaN(d)) return Math.floor(d.getTime() / 1000);
  } catch { /* fall through */ }
  // Best-effort fallback: File.lastModified (may be install time on
  // some phones but still useful for ordering).
  if (file.lastModified) return Math.floor(file.lastModified / 1000);
  return null;
}

/** Derive the "-Names.jpeg" filename that the backend will send back;
    used as a fallback if the response's X-Filename header is missing
    (e.g. reverse-proxy strips custom headers). */
function derivedName(srcName) {
  const stem = (srcName || "group.jpg").replace(/\.[^./]+$/, "");
  return `${stem}-Names.jpeg`;
}

/** Retry an axios call up to `tries` times on network / 5xx errors
    (Cloudflare 520/524/503/502 come through as 5xx). Backs off
    linearly. Client errors (4xx) fail fast. */
async function withRetry(fn, tries = 3) {
  let lastErr;
  for (let attempt = 0; attempt < tries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      // Don't retry 4xx (client errors — they'll never succeed).
      if (status && status >= 400 && status < 500) throw err;
      // Wait 500 ms · 1s · 1.5 s between retries.
      await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
    }
  }
  throw lastErr;
}

export default function GroupPortraitsDialog({ open, onClose }) {
  const fileRef = useRef(null);
  const [namesOverride, setNamesOverride] = useState("");
  const [items, setItems] = useState([]);      // per-file state
  const [activeIdx, setActiveIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [zipping, setZipping] = useState(false);
  // Persistent-job state: list of past jobs shown at the top of the
  // dialog, plus the current job's server-side id and pretty name.
  const [savedJobs, setSavedJobs] = useState([]);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobName, setJobName] = useState("");

  // Fetch the list of saved jobs whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/group-portraits/jobs`);
        setSavedJobs(data.jobs || []);
      } catch { /* silent — panel just stays empty */ }
    })();
  }, [open]);

  // Save the current job to the backend once matching is complete.
  const saveCurrentJob = async (opts = {}) => {
    if (!matchResults?.results?.length) return;
    const sheets = items
      .filter((it) => it.status === "done" && it.sessionId)
      .map((it) => ({
        source_filename: it.file?.name || it.outName,
        session_id: it.sessionId,
        face_count: it.meta?.faceCount ?? null,
        name_count: it.meta?.nameCount ?? null,
        names: it.meta?.names ?? [],
      }));
    // Compact the matches — thumb_b64 is small (96×96 jpg ≈ 4-8 KB).
    const compact = matchResults.results.map((r) => ({
      source: r.source, matched_name: r.matched_name || null,
      matched_via: r.matched_via || null,
      similarity: r.similarity ?? null,
      matched_source: r.matched_source || null,
      matched_session_id: r.matched_session_id || null,
      cluster_id: r.cluster_id ?? null,
      is_rep: !!r.is_rep, spot_verified: !!r.spot_verified,
      cluster_split: !!r.cluster_split,
      cluster_from: r.cluster_from || null,
      thumb_b64: r.thumb_b64 || null,
      error: r.error || null,
    }));
    // Cluster structure summary (rep + member filenames only — the
    // backend uses filenames as stable IDs across reloads).
    const clusterSummary = (() => {
      const map = new Map();
      compact.forEach((m) => {
        const cid = m.cluster_id;
        if (cid == null) return;
        if (!map.has(cid)) map.set(cid, { rep_source: null, member_sources: [] });
        const bucket = map.get(cid);
        bucket.member_sources.push(m.source);
        if (m.is_rep) bucket.rep_source = m.source;
      });
      return [...map.values()];
    })();
    try {
      const body = {
        job_id: currentJobId || undefined,
        name: jobName || opts.defaultName || undefined,
        status: opts.status || "done",
        sheets,
        match_results: compact,
        clusters: clusterSummary,
      };
      const { data } = await axios.post(`${API}/group-portraits/jobs`, body);
      if (!currentJobId && data.job_id) setCurrentJobId(data.job_id);
      // Refresh the summary panel.
      try {
        const { data: list } = await axios.get(`${API}/group-portraits/jobs`);
        setSavedJobs(list.jobs || []);
      } catch { /* ignore */ }
      return data.job_id;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "save failed";
      toast.error(`Couldn't save job: ${msg}`);
    }
  };

  const loadJob = async (job_id) => {
    try {
      const { data } = await axios.get(`${API}/group-portraits/jobs/${job_id}`);
      // Reset local state and rehydrate from the saved doc.
      setItems([]);
      setCurrentJobId(job_id);
      setJobName(data.name || "");
      // Reconstruct the match grid — no source File objects because
      // we don't store raw uploads; ZIP export is available for the
      // composite sheets + manifest only. Downloading renamed
      // portraits requires re-picking the source folder.
      const results = (data.match_results || []).map((r) => ({
        ...r, status: "done",
      }));
      applyResults(() => ({ pool: [], results }));
      setMatchFiles([]);
      setMatchProgress({ done: 0, total: results.length });
      toast.success(`Loaded "${data.name}" · ${results.length} photos`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "load failed";
      toast.error(`Couldn't load job: ${msg}`);
    }
  };

  const deleteJob = async (job_id) => {
    try {
      await axios.delete(`${API}/group-portraits/jobs/${job_id}`);
      setSavedJobs((prev) => prev.filter((j) => j.job_id !== job_id));
      if (currentJobId === job_id) setCurrentJobId(null);
      toast.success("Job deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const reset = () => {
    for (const it of items) {
      if (it.resultUrl) URL.revokeObjectURL(it.resultUrl);
      if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
    }
    setItems([]);
    setActiveIdx(0);
    setNamesOverride("");
    setCurrentJobId(null);
    setJobName("");
    setMatchFiles([]);
    setMatchResults(null);
  };
  const close = () => {
    reset();
    onClose?.();
  };

  const onPick = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) {
      toast.error("Please pick image files");
      return;
    }
    // Wipe previous batch.
    for (const it of items) {
      if (it.resultUrl) URL.revokeObjectURL(it.resultUrl);
      if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
    }
    const next = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      resultUrl: null,
      resultBlob: null,
      outName: derivedName(file.name),
      status: "queued",   // queued | running | done | failed
      error: null,
      meta: null,
    }));
    setItems(next);
    setActiveIdx(0);
  };

  // Process one file → mutate the items array at its index.
  const processOne = async (idx, override) => {
    setItems((prev) => prev.map((it, i) =>
      i === idx ? { ...it, status: "running", error: null } : it,
    ));
    const it = itemsRef.current[idx];
    try {
      const form = new FormData();
      form.append("file", it.file);
      form.append("names_override", override || "");
      const res = await axios.post(
        `${API}/group-portraits/generate`,
        form,
        { responseType: "blob" },
      );
      const outName = res.headers["x-filename"] || derivedName(it.file.name);
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      setItems((prev) => prev.map((x, i) =>
        i === idx ? {
          ...x,
          status: "done",
          resultBlob: blob,
          resultUrl: url,
          outName,
          sessionId: res.headers["x-session-id"] || "",
          meta: {
            faceCount: parseInt(res.headers["x-face-count"] || "0", 10),
            nameCount: parseInt(res.headers["x-name-count"] || "0", 10),
            source: res.headers["x-name-source"] || "gemini",
            names: res.headers["x-names"] || "",
          },
        } : x,
      ));
    } catch (err) {
      let msg = "Generation failed";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          msg = JSON.parse(text).detail || msg;
        } catch { /* keep default */ }
      } else {
        msg = err.response?.data?.detail || err.message || msg;
      }
      setItems((prev) => prev.map((x, i) =>
        i === idx ? { ...x, status: "failed", error: msg } : x,
      ));
    }
  };

  // Keep a live ref to items so processOne always sees the latest
  // state without re-firing when setItems runs mid-batch.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const generate = async () => {
    if (items.length === 0) {
      toast.error("Pick at least one image first");
      return;
    }
    setBusy(true);
    setActiveIdx(0);
    try {
      // Reset every item's result state.
      setItems((prev) => prev.map((it) => {
        if (it.resultUrl) URL.revokeObjectURL(it.resultUrl);
        return {
          ...it, status: "queued", resultUrl: null,
          resultBlob: null, meta: null, error: null,
        };
      }));
      // Run with concurrency cap. Count locally so the completion
      // toast doesn't race with unflushed React state via itemsRef.
      const queue = items.map((_, i) => i);
      let cursor = 0;
      let doneCount = 0;
      let failedCount = 0;
      const workers = new Array(Math.min(MAX_PARALLEL, queue.length))
        .fill(null)
        .map(async () => {
          while (cursor < queue.length) {
            const idx = queue[cursor++];
            await processOne(idx, namesOverride);
            const res = itemsRef.current[idx];
            if (res?.status === "done") doneCount++;
            else if (res?.status === "failed") failedCount++;
          }
        });
      await Promise.all(workers);
      if (failedCount === 0) {
        toast.success(`Generated ${doneCount} portrait sheet${doneCount === 1 ? "" : "s"}`);
      } else {
        toast.error(`${doneCount} done · ${failedCount} failed — see individual cards`);
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadCurrent = () => {
    const it = items[activeIdx];
    if (!it?.resultUrl) return;
    const a = document.createElement("a");
    a.href = it.resultUrl;
    a.download = it.outName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadAll = async () => {
    const done = items.filter((x) => x.status === "done" && x.resultBlob);
    if (done.length === 0) {
      toast.error("Nothing to download yet");
      return;
    }
    if (done.length === 1) {
      downloadCurrent();
      return;
    }
    setZipping(true);
    try {
      const zip = new JSZip();
      for (const it of done) {
        zip.file(it.outName, it.resultBlob);
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "group-portraits.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Zipped ${done.length} sheets`);
    } catch (err) {
      toast.error(`Zip failed: ${err.message}`);
    } finally {
      setZipping(false);
    }
  };

  const folderRef = useRef(null);   // Section 1 · group photos folder
  const matchFolderRef = useRef(null);   // Section 3 · portrait folder to rename
  const matchFilesRef = useRef(null);    // Section 3 · individual files fallback
  const [matching, setMatching] = useState(false);
  // Phase 100.2: streamed preview — files are processed one at a
  // time (with a small concurrency cap) so the UI can show which
  // photo is being renamed as it happens, in a mini carousel.
  const [matchFiles, setMatchFiles] = useState([]);       // File[]
  const [matchResults, setMatchResults] = useState(null); // {results,pool} | null
  const matchResultsSnapRef = useRef([]);                  // live results array
  const [matchProgress, setMatchProgress] = useState({ done: 0, total: 0 });
  const [recentMatches, setRecentMatches] = useState([]);  // last N for carousel
  const [zipMatch, setZipMatch] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);

  const sanitise = (s) =>
    (s || "Unknown").replace(/[^A-Za-z0-9._\- ]+/g, "").trim().replace(/\s+/g, "_") || "Unknown";

  // Keep the ref in sync with the latest results array so async
  // passes (clustering, AI fallback) can read fresh state without
  // waiting on React's batched re-renders.
  const applyResults = (updater) => {
    setMatchResults((prev) => {
      const next = updater(prev);
      matchResultsSnapRef.current = next?.results || [];
      return next;
    });
  };

  const runPreview = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const sessions = items
      .filter((it) => it.status === "done" && it.sessionId)
      .map((it) => it.sessionId);
    if (sessions.length === 0) {
      toast.error("Generate at least one portrait sheet first");
      return;
    }
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) {
      toast.error("Pick image files");
      return;
    }
    const sessionsStr = sessions.join(",");

    setMatching(true);
    setMatchFiles(files);
    setRecentMatches([]);
    applyResults(() => ({
      pool: [],
      results: files.map((f) => ({
        source: f.name, similarity: null, matched_name: null,
        thumb_b64: null, error: null, status: "queued",
      })),
    }));

    // ── STAGE 1 · Cluster ────────────────────────────────────────────
    // Group same-burst shots together using EXIF DateTimeOriginal (±15 s)
    // or filename number (±2). We only match ONE representative per
    // cluster later, saving 3-5× the CPU on a typical shoot.
    toast.message(`Clustering ${files.length} photos…`);
    const metas = await Promise.all(files.map(async (f, i) => ({
      idx: i,
      num: filenameNumber(f.name),
      ts: await fileTimestamp(f),
    })));
    // Sort by timestamp when available, else by filename number.
    const order = [...metas].sort((a, b) => {
      const ka = a.ts ?? (a.num ?? 0) * 60;
      const kb = b.ts ?? (b.num ?? 0) * 60;
      return ka - kb;
    });
    // Greedy walk builds runs of close-in-time / close-in-number shots.
    // When BOTH signals are available we require both to be close —
    // this stops an entire portrait shoot (all consecutive filename
    // numbers) collapsing into a single cluster. When only one signal
    // is available (e.g. EXIF stripped), we use it alone.
    const clusters = [];
    for (const cur of order) {
      const last = clusters[clusters.length - 1];
      let same = false;
      if (last) {
        const prev = last[last.length - 1];
        const hasSec = cur.ts != null && prev.ts != null;
        const hasNum = cur.num != null && prev.num != null;
        const gapSec = hasSec ? Math.abs(cur.ts - prev.ts) : Infinity;
        const gapNum = hasNum ? Math.abs(cur.num - prev.num) : Infinity;
        if (hasSec && hasNum) {
          same = gapSec <= CLUSTER_WINDOW_SEC && gapNum <= CLUSTER_NUMBER_GAP;
        } else if (hasSec) {
          same = gapSec <= CLUSTER_WINDOW_SEC;
        } else if (hasNum) {
          same = gapNum <= CLUSTER_NUMBER_GAP;
        }
      }
      if (same) last.push(cur);
      else clusters.push([cur]);
    }
    const repIdxOfCluster = clusters.map((c) => c[Math.floor(c.length / 2)].idx);
    const clusterIdxOfFile = new Array(files.length);
    clusters.forEach((c, cid) => c.forEach((m) => { clusterIdxOfFile[m.idx] = cid; }));
    applyResults((prev) => ({
      ...prev,
      results: prev.results.map((r, i) => ({
        ...r,
        cluster_id: clusterIdxOfFile[i],
        is_rep: repIdxOfCluster[clusterIdxOfFile[i]] === i,
      })),
    }));
    toast.message(`Formed ${clusters.length} cluster${clusters.length === 1 ? "" : "s"} from ${files.length} shots`);
    setMatchProgress({ done: 0, total: clusters.length });

    // Helper — write a match result to every file in a cluster.
    let carouselSeq = 0;
    const propagateCluster = (cid, matchInfo, via) => {
      const memberSet = new Set(clusters[cid].map((m) => m.idx));
      const repIdx = repIdxOfCluster[cid];
      const repName = files[repIdx].name;
      applyResults((prev) => ({
        ...prev,
        results: prev.results.map((r, i) => {
          if (!memberSet.has(i)) return r;
          if (i === repIdx) {
            // The rep row keeps its raw SFace/AI details.
            return {
              ...r,
              ...matchInfo,
              status: "done",
              matched_via: via === "ai" ? "ai" : undefined,
              error: matchInfo.matched_name ? null : (matchInfo.error || r.error),
            };
          }
          // Cluster members inherit only when the rep actually matched.
          if (matchInfo.matched_name) {
            return {
              ...r,
              matched_name: matchInfo.matched_name,
              matched_source: matchInfo.matched_source,
              matched_session_id: matchInfo.matched_session_id,
              similarity: matchInfo.similarity,
              matched_via: "cluster",
              cluster_from: repName,
              error: null,
              status: "done",
            };
          }
          return { ...r, status: "done" };
        }),
      }));
    };

    // ── STAGE 2 · Match cluster reps with SFace ──────────────────────
    const CONC = 2;
    let cursor = 0;
    const workers = new Array(Math.min(CONC, clusters.length)).fill(null).map(async () => {
      while (cursor < clusters.length) {
        const cid = cursor++;
        const repIdx = repIdxOfCluster[cid];
        const f = files[repIdx];
        try {
          const form = new FormData();
          form.append("session_ids", sessionsStr);
          form.append("files", f);
          const { data } = await withRetry(() => axios.post(
            `${API}/group-portraits/match-preview`, form,
          ));
          const row = data.results?.[0] || { source: f.name, error: "empty response" };
          const pool = data.pool || [];
          applyResults((prev) => ({
            pool: prev?.pool?.length ? prev.pool : pool,
            results: prev.results,
          }));
          propagateCluster(cid, row, "sface");
          if (row.matched_name) {
            const seq = ++carouselSeq;
            setRecentMatches((prev) => [
              { idx: repIdx, seq, ...row, at: Date.now() },
              ...prev.slice(0, 7),
            ]);
          }
        } catch (err) {
          const msg = err.response?.data?.detail || err.message || "request failed";
          propagateCluster(cid, { source: f.name, error: msg }, "sface");
        } finally {
          setMatchProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      }
    });
    await Promise.all(workers);

    // ── STAGE 2.5 · Verify both endpoints of every cluster ───────────
    // For any cluster with ≥ 2 members whose rep matched confidently,
    // rerun SFace on the FIRST and LAST member (in the sorted order,
    // which combines EXIF time + filename number). If either disagrees
    // with the rep, split the cluster — every remaining member gets
    // matched individually.
    const toVerify = [];
    for (let cid = 0; cid < clusters.length; cid++) {
      if (clusters[cid].length < 2) continue;
      const rep = matchResultsSnapRef.current[repIdxOfCluster[cid]];
      if (!rep?.matched_name) continue;
      if (rep.similarity != null && rep.similarity < CONFIDENT_SIM) continue;
      const repIdx = repIdxOfCluster[cid];
      const cluster = clusters[cid];
      const endpoints = [
        cluster[0].idx,
        cluster[cluster.length - 1].idx,
      ].filter((i) => i !== repIdx);
      // Dedupe (a 2-member cluster's endpoints overlap).
      const verifierIdxs = [...new Set(endpoints)];
      if (verifierIdxs.length === 0) continue;
      toVerify.push({ cid, verifierIdxs, repName: rep.matched_name });
    }

    let splitCount = 0;
    let verifiedCount = 0;
    if (toVerify.length > 0) {
      toast.message(`Verifying ${toVerify.length} cluster${toVerify.length === 1 ? "" : "s"} at both ends…`);
      const VER_CONC = 2;
      let vcursor = 0;
      const verifyWorkers = new Array(Math.min(VER_CONC, toVerify.length)).fill(null).map(async () => {
        while (vcursor < toVerify.length) {
          const { cid, verifierIdxs, repName } = toVerify[vcursor++];
          // Match every endpoint in parallel.
          const results = await Promise.all(verifierIdxs.map(async (vIdx) => {
            try {
              const form = new FormData();
              form.append("session_ids", sessionsStr);
              form.append("files", files[vIdx]);
              const { data } = await withRetry(() => axios.post(
                `${API}/group-portraits/match-preview`, form,
              ));
              return { vIdx, row: data.results?.[0] || null };
            } catch {
              return { vIdx, row: null };
            }
          }));

          const allAgree = results.every(({ row }) =>
            row?.matched_name === repName
            && (row.similarity == null || row.similarity >= CONFIDENT_SIM)
          );

          if (allAgree) {
            verifiedCount++;
            // Flag every verified endpoint with a ✓ badge.
            applyResults((prev) => ({
              ...prev,
              results: prev.results.map((r, i) => {
                const hit = results.find((x) => x.vIdx === i);
                if (!hit) return r;
                return { ...r, ...(hit.row || {}), spot_verified: true, status: "done" };
              }),
            }));
          } else {
            // Split the cluster — first write the endpoints' own SFace
            // results so we don't refetch them, then match every OTHER
            // non-rep member individually.
            splitCount++;
            const repIdx = repIdxOfCluster[cid];
            const seenIdxs = new Set(verifierIdxs);
            applyResults((prev) => ({
              ...prev,
              results: prev.results.map((r, i) => {
                const hit = results.find((x) => x.vIdx === i);
                if (!hit) return r;
                return {
                  ...r, ...(hit.row || {}),
                  matched_via: undefined,
                  cluster_from: undefined,
                  cluster_split: true,
                  status: "done",
                };
              }),
            }));
            const remaining = clusters[cid]
              .map((m) => m.idx)
              .filter((i) => i !== repIdx && !seenIdxs.has(i));
            for (const memberIdx of remaining) {
              try {
                const mForm = new FormData();
                mForm.append("session_ids", sessionsStr);
                mForm.append("files", files[memberIdx]);
                const { data: mData } = await withRetry(() => axios.post(
                  `${API}/group-portraits/match-preview`, mForm,
                ));
                const mRow = mData.results?.[0] || { error: "empty response" };
                applyResults((prev) => ({
                  ...prev,
                  results: prev.results.map((r, i) =>
                    i === memberIdx
                      ? {
                          ...r, ...mRow,
                          matched_via: undefined,
                          cluster_from: undefined,
                          cluster_split: true,
                          status: "done",
                        }
                      : r,
                  ),
                }));
              } catch { /* leave row */ }
            }
          }
        }
      });
      await Promise.all(verifyWorkers);
      if (splitCount > 0) {
        toast.message(`Split ${splitCount} mixed cluster${splitCount === 1 ? "" : "s"} — remapping individual photos`);
      }
    }

    // ── STAGE 3 · AI fallback on unresolved clusters ─────────────────
    // For each unresolved cluster, we send Gemini a narrowed candidate
    // list built from the ±3 adjacent clusters' confidently-matched
    // names — so it isn't wasting time comparing against strangers.
    //
    // BUT: AI is slow, so we PROMPT the user before running it. If
    // they skip, we stop after the SFace + verification pass and let
    // them proceed straight to ZIP download.
    const latest = matchResultsSnapRef.current;
    const isConfident = (r) =>
      r?.matched_name && (r.similarity == null || r.similarity >= CONFIDENT_SIM);
    const unresolvedClusters = [];
    for (let cid = 0; cid < clusters.length; cid++) {
      const r = latest[repIdxOfCluster[cid]];
      // Skip clusters whose rep is a "no face" shot — those are
      // deliberately unnameable (nameplate, mistake, backdrop).
      if (r?.error === "no face detected") continue;
      const weak = !r?.matched_name
        || (r.similarity != null && r.similarity < AI_TRIGGER);
      if (weak) unresolvedClusters.push(cid);
    }
    // Rough count of files that WOULD be sent to AI so we can size
    // the prompt honestly ("~1 s per photo").
    const wouldCallAiCount = unresolvedClusters.length
      + latest.filter((r) => !r?.matched_name && r?.error !== "no face detected").length;

    let wantAi = false;
    if (wouldCallAiCount > 0) {
      // Persistent toast with Yes/Skip actions. Resolves when clicked.
      wantAi = await new Promise((resolve) => {
        const tid = toast(
          `${wouldCallAiCount} photo${wouldCallAiCount === 1 ? "" : "s"} still unmatched. Run AI matching?`,
          {
            description: `Estimated ~${Math.max(1, Math.round(wouldCallAiCount * 1.5))} seconds using Gemini. Skip to go straight to ZIP.`,
            duration: Infinity,
            action: {
              label: "Run AI",
              onClick: () => { toast.dismiss(tid); resolve(true); },
            },
            cancel: {
              label: "Skip",
              onClick: () => { toast.dismiss(tid); resolve(false); },
            },
          },
        );
      });
    }

    let aiResolved = 0;
    let finalAiResolved = 0;
    if (wantAi && unresolvedClusters.length > 0) {
      toast.message(`Asking AI to identify ${unresolvedClusters.length} tricky cluster${unresolvedClusters.length === 1 ? "" : "s"}…`);
      const AI_CONC = 2;
      let acursor = 0;
      const aiWorkers = new Array(Math.min(AI_CONC, unresolvedClusters.length)).fill(null).map(async () => {
        while (acursor < unresolvedClusters.length) {
          const cid = unresolvedClusters[acursor++];
          const repIdx = repIdxOfCluster[cid];
          const f = files[repIdx];
          // Build ±3 neighbour candidate list.
          const candidates = new Set();
          for (let d = 1; d <= 3; d++) {
            for (const nk of [cid - d, cid + d]) {
              if (nk < 0 || nk >= clusters.length) continue;
              const nr = matchResultsSnapRef.current[repIdxOfCluster[nk]];
              if (isConfident(nr) || nr?.matched_via === "ai") {
                candidates.add(nr.matched_name);
              }
            }
          }
          try {
            const form = new FormData();
            form.append("session_ids", sessionsStr);
            form.append("files", f);
            if (candidates.size > 0) {
              form.append("candidate_names", [...candidates].join(","));
            }
            const { data } = await withRetry(() => axios.post(
              `${API}/group-portraits/match-ai`, form,
            ));
            const row = data.results?.[0];
            if (row?.matched_name) {
              aiResolved++;
              propagateCluster(cid, {
                source: f.name,
                matched_name: row.matched_name,
                similarity: null,
                matched_source: null,
                matched_session_id: null,
              }, "ai");
              const seq = ++carouselSeq;
              setRecentMatches((prev) => [
                { idx: repIdx, seq, source: f.name, matched_name: row.matched_name,
                  matched_via: "ai", similarity: null, at: Date.now() },
                ...prev.slice(0, 7),
              ]);
            }
          } catch { /* leave rep as-is */ }
        }
      });
      await Promise.all(aiWorkers);
    }

    // ── STAGE 4 · Final AI sweep on ALL still-unmatched files ───────
    // The user's ask: any file that is still without a name after
    // clustering + verification + rep-AI gets one more shot via
    // Gemini, with candidate names drawn from the confidently-matched
    // ±3 filename/time neighbours.
    const finalUnmatched = [];
    for (let i = 0; i < files.length; i++) {
      const r = matchResultsSnapRef.current[i];
      // Skip files where SFace explicitly returned "no face" — those
      // are deliberately unnameable and shouldn't get AI guesses.
      if (r?.error === "no face detected") continue;
      if (!r?.matched_name) finalUnmatched.push(i);
    }
    // Build a lookup: file idx → sorted position in the EXIF/filename order
    const orderPos = new Array(files.length);
    order.forEach((m, k) => { orderPos[m.idx] = k; });

    if (wantAi && finalUnmatched.length > 0) {
      toast.message(`Final AI sweep · ${finalUnmatched.length} photo${finalUnmatched.length === 1 ? "" : "s"} still unmatched…`);
      const F_CONC = 2;
      let fcursor = 0;
      const fWorkers = new Array(Math.min(F_CONC, finalUnmatched.length)).fill(null).map(async () => {
        while (fcursor < finalUnmatched.length) {
          const idx = finalUnmatched[fcursor++];
          // Candidate names from ±3 neighbours in the sorted order.
          const pos = orderPos[idx];
          const cands = new Set();
          for (let d = 1; d <= 3; d++) {
            for (const nk of [pos - d, pos + d]) {
              if (nk < 0 || nk >= order.length) continue;
              const nr = matchResultsSnapRef.current[order[nk].idx];
              if (nr?.matched_name) cands.add(nr.matched_name);
            }
          }
          try {
            const form = new FormData();
            form.append("session_ids", sessionsStr);
            form.append("files", files[idx]);
            if (cands.size > 0) {
              form.append("candidate_names", [...cands].join(","));
            }
            const { data } = await withRetry(() => axios.post(
              `${API}/group-portraits/match-ai`, form,
            ));
            const row = data.results?.[0];
            if (row?.matched_name) {
              finalAiResolved++;
              applyResults((prev) => ({
                ...prev,
                results: prev.results.map((r, i) =>
                  i === idx
                    ? {
                        ...r,
                        matched_name: row.matched_name,
                        matched_via: "ai",
                        error: null,
                        status: "done",
                      }
                    : r,
                ),
              }));
              const seq = ++carouselSeq;
              setRecentMatches((prev) => [
                { idx, seq, source: files[idx].name, matched_name: row.matched_name,
                  matched_via: "ai", similarity: null, at: Date.now() },
                ...prev.slice(0, 7),
              ]);
            }
          } catch { /* leave as-is */ }
        }
      });
      await Promise.all(fWorkers);
    }

    setMatching(false);
    const totalMatched = matchResultsSnapRef.current.filter((r) => r?.matched_name).length;
    toast.success(
      `Done · ${totalMatched}/${files.length} named across ${clusters.length} cluster${clusters.length === 1 ? "" : "s"}` +
      (splitCount || aiResolved || finalAiResolved
        ? ` (split ${splitCount}, AI ${aiResolved + finalAiResolved})`
        : ""),
    );
    // Persist the job so the user can come back to review / download
    // it in the next 7 days without losing progress.
    const firstSheet = items.find((it) => it.status === "done" && it.sessionId);
    const defaultName = firstSheet
      ? `${firstSheet.file?.name?.replace(/\.[^.]+$/, "") || "Group"} · ${files.length} shots`
      : `Job · ${new Date().toLocaleString()}`;
    saveCurrentJob({ defaultName });
  };

  // Override a single row's match (or clear it) in the preview grid.
  const overrideMatch = (idx, name) => {
    if (!matchResults) return;
    applyResults((prev) => ({
      ...prev,
      results: prev.results.map((r, i) =>
        i === idx ? { ...r, matched_name: name || null, overridden: true } : r,
      ),
    }));
    // Debounced-ish auto-save so manual tweaks persist across sessions.
    if (currentJobId) {
      clearTimeout(overrideMatch._t);
      overrideMatch._t = setTimeout(() => saveCurrentJob(), 800);
    }
  };

  const downloadMatchedZip = async () => {
    if (!matchResults || matchFiles.length === 0) return;
    setZipMatch(true);
    setZipProgress(0);
    try {
      const zip = new JSZip();
      const rows = matchResults.results;
      // Guardrails: async chunked add so the UI stays responsive on
      // hundreds of files.
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        const src = matchFiles[i];
        if (!src) continue;
        const dot = src.name.lastIndexOf(".");
        const stem = dot > 0 ? src.name.slice(0, dot) : src.name;
        const ext = dot > 0 ? src.name.slice(dot + 1) : "jpg";
        if (r.matched_name) {
          zip.file(`${stem}_${sanitise(r.matched_name)}.${ext}`, src);
        } else {
          zip.file(`_unmatched/${src.name}`, src);
        }
        // Yield to event loop every 25 files so the UI can repaint
        // progress and stay responsive on huge folders.
        if (i % 25 === 0) {
          setZipProgress(Math.round((i / rows.length) * 100));
          await new Promise((res) => setTimeout(res, 0));
        }
      }
      // Manifest for auditability.
      const manifest = ["source,matched_name,similarity,overridden"]
        .concat(rows.map((r, i) =>
          `${matchFiles[i]?.name || ""},"${r.matched_name || ""}",${r.similarity ?? "nan"},${r.overridden ? 1 : 0}`,
        ))
        .join("\n");
      zip.file("manifest.csv", manifest);
      // Also include the generated portrait sheets, named
      // <group source>_Composit.jpg so photographers keep the
      // reference alongside the renamed shots.
      for (const it of items) {
        if (it.status !== "done" || !it.resultBlob) continue;
        const src = it.file?.name || "group.jpg";
        const dot = src.lastIndexOf(".");
        const gstem = dot > 0 ? src.slice(0, dot) : src;
        zip.file(`_composit/${gstem}_Composit.jpg`, it.resultBlob);
      }
      const blob = await zip.generateAsync(
        { type: "blob", compression: "DEFLATE" },
        (m) => setZipProgress(Math.round(m.percent)),
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "portraits-renamed.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP ready");
    } catch (err) {
      toast.error(`ZIP failed: ${err.message}`);
    } finally {
      setZipMatch(false);
      setZipProgress(0);
    }
  };

  if (!open) return null;

  const current = items[activeIdx];
  const doneCount = items.filter((x) => x.status === "done").length;
  const total = items.length;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-black/70 backdrop-blur-sm p-[2%] flex items-center justify-center"
      style={{ pointerEvents: "auto" }}
      data-testid="group-portraits-dialog"
      onKeyDown={(e) => { if (e.key === "Escape") close(); }}
      tabIndex={-1}
    >
      <div className="bg-slate-950 text-slate-100 rounded-lg w-[92vw] max-w-6xl h-[92vh] flex flex-col overflow-hidden shadow-2xl border border-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-2">
            <UsersRound className="h-4 w-4 text-slate-400" />
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
              Group → Portraits
            </span>
            {total > 0 && (
              <span className="text-[10px] font-mono text-slate-500">
                · {doneCount}/{total} ready
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="group-portraits-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 grid grid-cols-[minmax(0,1fr)_360px] overflow-hidden">
          {/* Left: preview / result canvas */}
          <div className="overflow-auto p-4 bg-slate-900/40">
            {items.length === 0 && (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-slate-700 rounded-lg h-full min-h-[60vh] flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-primary hover:bg-slate-900/60 transition-colors"
                data-testid="group-portraits-drop"
              >
                <Upload className="h-8 w-8 text-slate-500" />
                <p className="text-sm text-slate-400 font-medium">
                  Drop group photos here — or click to browse
                </p>
                <p className="text-[11px] text-slate-500 max-w-md text-center">
                  Select multiple wide lineup photos at once. Each will be turned into
                  a labelled portrait sheet named &lt;original&gt;-Names.jpeg.
                </p>
              </div>
            )}

            {matchResults && (
              <MatchStreamPreview
                results={matchResults.results}
                pool={matchResults.pool}
                recent={recentMatches}
                progress={matchProgress}
                matching={matching}
                onOverride={overrideMatch}
              />
            )}

            {current && !matchResults && (
              <div className="space-y-3">
                {/* Traversal controls */}
                {items.length > 1 && (
                  <div className="flex items-center justify-between gap-2 bg-slate-900 border border-slate-800 rounded-sm p-1.5">
                    <button
                      type="button"
                      onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
                      disabled={activeIdx === 0}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-800 disabled:opacity-30 text-slate-300"
                      data-testid="group-portraits-prev"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </button>
                    <div className="flex-1 text-center">
                      <div className="text-[11px] font-mono tabular-nums text-slate-300 truncate">
                        {current.outName}
                      </div>
                      <div className="text-[9px] uppercase tracking-widest text-slate-500">
                        {activeIdx + 1} of {items.length}
                        {current.status === "queued" && " · queued"}
                        {current.status === "running" && " · generating…"}
                        {current.status === "failed" && " · failed"}
                        {current.status === "done" && current.meta && (
                          <> · {current.meta.faceCount} face{current.meta.faceCount === 1 ? "" : "s"} · {current.meta.nameCount} name{current.meta.nameCount === 1 ? "" : "s"}</>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveIdx((i) => Math.min(items.length - 1, i + 1))}
                      disabled={activeIdx === items.length - 1}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-800 disabled:opacity-30 text-slate-300"
                      data-testid="group-portraits-next"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                {/* Result or fallback preview */}
                {current.status === "done" && current.resultUrl ? (
                  <img
                    src={current.resultUrl}
                    alt="Portrait grid"
                    className="max-w-full max-h-[65vh] block rounded-sm border border-slate-800 mx-auto"
                    data-testid="group-portraits-result"
                  />
                ) : current.status === "running" ? (
                  <div className="relative">
                    <img
                      src={current.previewUrl}
                      alt="Source"
                      className="max-w-full max-h-[65vh] block rounded-sm border border-slate-800 mx-auto opacity-50"
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      <span className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
                        Reading names + cropping faces…
                      </span>
                    </div>
                  </div>
                ) : current.status === "failed" ? (
                  <div className="relative">
                    <img
                      src={current.previewUrl}
                      alt="Source"
                      className="max-w-full max-h-[65vh] block rounded-sm border border-slate-800 mx-auto opacity-40"
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                      <AlertCircle className="h-6 w-6 text-red-400" />
                      <span className="text-[11px] font-bold uppercase tracking-widest text-red-300">
                        {current.error || "Failed"}
                      </span>
                      <button
                        type="button"
                        onClick={() => processOne(activeIdx, namesOverride)}
                        className="h-7 px-3 mt-1 rounded-sm border border-slate-700 text-slate-300 text-[10px] font-bold uppercase tracking-widest hover:bg-slate-800"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                ) : (
                  <img
                    src={current.previewUrl}
                    alt="Source"
                    className="max-w-full max-h-[65vh] block rounded-sm border border-slate-800 mx-auto"
                  />
                )}
              </div>
            )}
          </div>

          {/* Right: controls */}
          <div className="border-l border-slate-800 p-4 overflow-y-auto space-y-4">
            {/* Recent Jobs — persistent Group→Name runs from the past
                7 days. Click to reload; delete to prune. */}
            <div className="pb-3 border-b border-slate-800/60" data-testid="group-portraits-jobs-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  Recent Jobs · 7-day retention
                </p>
                <button
                  type="button"
                  onClick={reset}
                  className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-primary"
                  data-testid="group-portraits-new-job"
                  title="Start a fresh job"
                >
                  + New
                </button>
              </div>
              {savedJobs.length === 0 ? (
                <p className="text-[10px] text-slate-600 italic">
                  No saved jobs yet. Finish matching a folder to auto-save here.
                </p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {savedJobs.map((j) => {
                    const active = j.job_id === currentJobId;
                    return (
                      <div
                        key={j.job_id}
                        className={`group flex items-center gap-2 px-2 py-1.5 rounded-sm border text-[11px] cursor-pointer ${
                          active
                            ? "border-primary bg-primary/10"
                            : "border-slate-800 hover:border-slate-700 hover:bg-slate-800/40"
                        }`}
                        onClick={() => loadJob(j.job_id)}
                        data-testid={`group-portraits-job-${j.job_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className={`truncate font-semibold ${active ? "text-primary" : "text-slate-300"}`}>
                            {j.name}
                          </div>
                          <div className="text-[9px] font-mono tabular-nums text-slate-500">
                            {j.num_matched}/{j.num_files} named · {j.num_sheets} sheet{j.num_sheets === 1 ? "" : "s"} · {j.num_clusters} cluster{j.num_clusters === 1 ? "" : "s"}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${j.name}"?`)) deleteJob(j.job_id);
                          }}
                          className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 shrink-0"
                          title="Delete this job"
                          data-testid={`group-portraits-job-delete-${j.job_id}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {currentJobId && (
                <div className="mt-2 flex items-center gap-1.5">
                  <input
                    type="text"
                    value={jobName}
                    onChange={(e) => setJobName(e.target.value)}
                    onBlur={() => currentJobId && saveCurrentJob()}
                    placeholder="Rename this job…"
                    className="flex-1 h-7 px-2 rounded-sm border border-slate-800 bg-slate-950 text-[11px] text-slate-300 focus:outline-none focus:border-primary"
                    data-testid="group-portraits-job-rename"
                  />
                </div>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
                1 · Group photos
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => onPick(e.target.files)}
                data-testid="group-portraits-file-input"
              />
              <input
                ref={folderRef}
                type="file"
                webkitdirectory="true"
                multiple
                className="hidden"
                onChange={(e) => onPick(e.target.files)}
                data-testid="group-portraits-folder-input"
              />
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="h-9 inline-flex items-center justify-center gap-1.5 rounded-sm border border-slate-700 text-slate-300 text-[11px] font-bold uppercase tracking-widest hover:bg-slate-800"
                  data-testid="group-portraits-pick"
                  title="Pick individual files"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {items.length > 0 ? `Replace (${items.length})` : "Files"}
                </button>
                <button
                  type="button"
                  onClick={() => folderRef.current?.click()}
                  className="h-9 inline-flex items-center justify-center gap-1.5 rounded-sm border border-slate-700 text-slate-300 text-[11px] font-bold uppercase tracking-widest hover:bg-slate-800"
                  data-testid="group-portraits-pick-folder"
                  title="Pick a whole folder of group photos"
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                  Folder
                </button>
              </div>
              {items.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto space-y-1 pr-1">
                  {items.map((it, i) => (
                    <button
                      type="button"
                      key={it.file.name + i}
                      onClick={() => setActiveIdx(i)}
                      className={`w-full flex items-center gap-1.5 h-7 px-2 rounded-sm text-[10px] text-left ${
                        i === activeIdx
                          ? "bg-slate-800 text-slate-100"
                          : "text-slate-400 hover:bg-slate-900"
                      }`}
                    >
                      <StatusDot status={it.status} />
                      <span className="truncate flex-1">{it.file.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
                2 · Names override (optional)
              </p>
              <textarea
                value={namesOverride}
                onChange={(e) => setNamesOverride(e.target.value)}
                rows={6}
                placeholder="If AI reads a name wrong, type the corrected list here — comma or newline separated. Applied to the CURRENT photo when you click Retry (or all photos if you regenerate)."
                className="w-full bg-slate-900 border border-slate-700 rounded-sm p-2 text-[12px] font-mono text-slate-200 focus:outline-none focus:border-primary resize-none"
                data-testid="group-portraits-names"
              />
              {current?.meta?.names && (
                <p className="text-[10px] text-slate-500 mt-1.5">
                  AI read: <span className="text-slate-400 italic">{current.meta.names}</span>
                </p>
              )}
            </div>

            <div className="pt-2 border-t border-slate-800 space-y-2">
              <button
                type="button"
                onClick={generate}
                disabled={busy || items.length === 0}
                className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-white text-[11px] font-bold uppercase tracking-widest hover:bg-primary/90 disabled:bg-slate-700 disabled:cursor-not-allowed"
                data-testid="group-portraits-generate"
              >
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {doneCount > 0 && !busy ? "Regenerate all" : `Generate${items.length > 1 ? ` (${items.length})` : ""}`}
              </button>
              {doneCount > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={downloadCurrent}
                    disabled={current?.status !== "done"}
                    className="h-9 inline-flex items-center justify-center gap-1.5 rounded-sm border border-emerald-500 text-emerald-300 text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-950 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="group-portraits-download-current"
                  >
                    <Download className="h-3 w-3" />
                    This
                  </button>
                  <button
                    type="button"
                    onClick={downloadAll}
                    disabled={zipping || doneCount === 0}
                    className="h-9 inline-flex items-center justify-center gap-1.5 rounded-sm border border-emerald-500 text-emerald-300 text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-950 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="group-portraits-download-all"
                  >
                    {zipping ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Archive className="h-3 w-3" />
                    )}
                    All ({doneCount})
                  </button>
                </div>
              )}
            </div>

            <p className="text-[10px] text-slate-500 leading-relaxed pt-2">
              Faces are detected with OpenCV YuNet. Captions are read by Gemini
              vision. Output files are named <code className="text-slate-400">&lt;original&gt;-Names.jpeg</code>.
              Up to {MAX_PARALLEL} photos process in parallel.
            </p>

            {/* Phase 100: match & rename a folder of individual images
                against ALL generated sheets' sessions. Preview first,
                let the user override, then build the ZIP client-side
                so hundreds of photos don't clog the API. */}
            {items.some((it) => it.status === "done" && it.sessionId) && (
              <div className="pt-3 border-t border-slate-800 space-y-2">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  3 · Match &amp; rename a folder
                </p>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  Pick the folder of individual portraits. Files are clustered
                  by shot time / filename number first, then each cluster
                  is matched to a person on the sheet — same-burst photos
                  inherit the same name automatically.
                </p>
                <input
                  ref={matchFolderRef}
                  type="file"
                  webkitdirectory="true"
                  multiple
                  className="hidden"
                  onChange={(e) => runPreview(e.target.files)}
                  data-testid="group-portraits-match-folder-input"
                />
                <input
                  ref={matchFilesRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => runPreview(e.target.files)}
                  data-testid="group-portraits-match-input"
                />
                <div className="grid grid-cols-3 gap-1.5">
                  <button
                    type="button"
                    onClick={() => matchFolderRef.current?.click()}
                    disabled={matching}
                    className="col-span-2 h-10 inline-flex items-center justify-center gap-2 rounded-sm border border-primary bg-primary/10 text-primary text-[11px] font-bold uppercase tracking-widest hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="group-portraits-match-btn"
                    title="Pick a folder of portrait photos"
                  >
                    {matching ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FolderOpen className="h-3.5 w-3.5" />
                    )}
                    {matching ? "Matching…" : (matchResults ? "Change folder" : "Pick folder")}
                  </button>
                  <button
                    type="button"
                    onClick={() => matchFilesRef.current?.click()}
                    disabled={matching}
                    className="h-10 inline-flex items-center justify-center gap-2 rounded-sm border border-slate-700 text-slate-400 text-[11px] font-bold uppercase tracking-widest hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="group-portraits-match-files-btn"
                    title="Pick individual portrait files"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    Files
                  </button>
                </div>
                {matchResults && (
                  <button
                    type="button"
                    onClick={downloadMatchedZip}
                    disabled={zipMatch}
                    className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-sm border border-emerald-500 text-emerald-300 text-[11px] font-bold uppercase tracking-widest hover:bg-emerald-950 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="group-portraits-download-matched"
                  >
                    {zipMatch ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Archive className="h-3.5 w-3.5" />
                    )}
                    {zipMatch
                      ? `Zipping… ${zipProgress}%`
                      : `Download renamed (${matchResults.results.filter((r) => r.matched_name).length}/${matchResults.results.length})`}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function StatusDot({ status }) {
  if (status === "done") return <CheckCircle2 className="h-3 w-3 text-emerald-400 shrink-0" />;
  if (status === "running") return <Loader2 className="h-3 w-3 text-primary animate-spin shrink-0" />;
  if (status === "failed") return <AlertCircle className="h-3 w-3 text-red-400 shrink-0" />;
  return <span className="h-2 w-2 rounded-full bg-slate-600 shrink-0 ml-0.5" />;
}


/** Streamed preview UI for the folder-match step.
 *
 * Layout:
 *   1. Progress bar (Matching N/M…) + status
 *   2. Mini carousel of the most recent 8 matches — newest slides in
 *      from the left; each card shows the source filename → matched
 *      name and the face thumbnail.
 *   3. Full editable grid (grows as matches arrive) with override
 *      dropdowns + similarity colour codes.
 */
function MatchStreamPreview({ results, pool, recent, progress, matching, onOverride }) {
  const [q, setQ] = useState("");
  const filtered = q
    ? results
        .map((r, i) => ({ r, i }))
        .filter(({ r }) => (r.source || "").toLowerCase().includes(q.toLowerCase())
                        || (r.matched_name || "").toLowerCase().includes(q.toLowerCase()))
    : results.map((r, i) => ({ r, i }));

  const matched = results.filter((r) => r.matched_name).length;
  const pct = progress.total > 0
    ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="space-y-3">
      {/* Progress bar */}
      <div className="bg-slate-900 border border-slate-800 rounded-sm p-3 space-y-2">
        <div className="flex items-center justify-between text-[11px]">
          <span className="uppercase tracking-widest font-bold text-slate-300">
            {matching ? "Matching clusters" : "Match complete"}
          </span>
          <span className="font-mono tabular-nums text-slate-400">
            {progress.done} / {progress.total} clusters · {matched} named
          </span>
        </div>
        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              matching ? "bg-primary" : "bg-emerald-500"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Carousel of recent matches */}
      {recent.length > 0 && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-2 overflow-hidden">
          <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 px-1">
            Just renamed
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="group-portraits-carousel">
            {recent.map((m) => (
              <div
                key={`${m.seq}-${m.idx}`}
                className="shrink-0 w-[220px] bg-slate-950 border border-slate-800 rounded-sm p-2 flex items-center gap-2 animate-in slide-in-from-left-4 fade-in duration-300"
              >
                {m.thumb_b64 ? (
                  <img
                    src={`data:image/jpeg;base64,${m.thumb_b64}`}
                    alt=""
                    className="w-10 h-10 rounded-sm object-cover shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-sm bg-slate-800 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-slate-400 truncate" title={m.source}>
                    {m.source}
                  </div>
                  <div className={`text-[11px] font-semibold truncate ${
                    m.matched_name ? "text-emerald-300" : "text-slate-500 italic"
                  }`}>
                    → {m.matched_name || "no match"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full grid */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
          Preview grid · pool of {pool.length} people
        </p>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by filename or name…"
          className="h-8 px-2 rounded-sm bg-slate-900 border border-slate-700 text-[11px] text-slate-200 focus:outline-none focus:border-primary w-[240px]"
          data-testid="group-portraits-match-filter"
        />
      </div>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}
        data-testid="group-portraits-match-grid"
      >
        {filtered.map(({ r, i }) => {
          const sim = r.similarity;
          const simColor = sim == null
            ? "text-slate-500"
            : sim >= 0.55 ? "text-emerald-400"
            : sim >= 0.35 ? "text-amber-400"
            : "text-red-400";
          return (
            <div
              key={i}
              className={`bg-slate-900 border rounded-sm p-2 flex items-start gap-2 transition-colors ${
                r.status === "queued"
                  ? "border-slate-800/50 opacity-50"
                  : "border-slate-800"
              }`}
            >
              {r.thumb_b64 ? (
                <img
                  src={`data:image/jpeg;base64,${r.thumb_b64}`}
                  alt=""
                  className="w-12 h-12 rounded-sm object-cover shrink-0"
                />
              ) : r.status === "queued" ? (
                <div className="w-12 h-12 rounded-sm bg-slate-800 flex items-center justify-center shrink-0">
                  <Loader2 className="h-3 w-3 animate-spin text-slate-500" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-sm bg-slate-800 flex items-center justify-center text-[9px] text-slate-500 shrink-0">
                  n/a
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-slate-300 truncate" title={r.source}>
                  {r.source}
                </div>
                {r.status === "queued" ? (
                  <div className="text-[10px] text-slate-500 italic mt-0.5">
                    waiting…
                  </div>
                ) : r.error ? (
                  <div className="text-[10px] text-red-400 italic mt-0.5">
                    {r.error}
                  </div>
                ) : (
                  <select
                    value={r.matched_name || ""}
                    onChange={(e) => onOverride(i, e.target.value)}
                    className="w-full h-6 mt-0.5 px-1 rounded-sm bg-slate-950 border border-slate-700 text-[11px] text-slate-200 focus:outline-none focus:border-primary"
                    data-testid={`group-portraits-match-select-${i}`}
                  >
                    <option value="">— unmatched —</option>
                    {pool.map((p) => (
                      <option
                        key={`${p.session_id}::${p.name}`}
                        value={p.name}
                      >
                        {p.name}
                        {p.source_stem && pool.some((q) => q.name === p.name && q.session_id !== p.session_id)
                          ? ` · ${p.source_stem}` : ""}
                      </option>
                    ))}
                  </select>
                )}
                {r.status !== "queued" && (
                  <div className={`text-[10px] font-mono tabular-nums mt-0.5 flex items-center gap-1.5 ${simColor}`}>
                    <span>sim {sim == null ? "—" : sim.toFixed(3)}</span>
                    {r.matched_via === "ai" && (
                      <span
                        className="inline-flex items-center gap-0.5 px-1 py-[1px] rounded-sm bg-violet-950/60 border border-violet-800 text-violet-300 text-[9px]"
                        title="Identified by AI fallback"
                      >
                        <Sparkles className="h-2.5 w-2.5" /> AI
                      </span>
                    )}
                    {r.matched_via === "cluster" && (
                      <span
                        className="px-1 py-[1px] rounded-sm bg-cyan-950/60 border border-cyan-800 text-cyan-300 text-[9px]"
                        title={r.cluster_from ? `Inherited from neighbour ${r.cluster_from}` : "Propagated from a nearby shot"}
                      >
                        ↺ cluster
                      </span>
                    )}
                    {r.spot_verified && (
                      <span
                        className="px-1 py-[1px] rounded-sm bg-emerald-950/60 border border-emerald-800 text-emerald-300 text-[9px]"
                        title="Random spot-check confirmed the cluster"
                      >
                        ✓ verified
                      </span>
                    )}
                    {r.cluster_split && (
                      <span
                        className="px-1 py-[1px] rounded-sm bg-amber-950/60 border border-amber-800 text-amber-300 text-[9px]"
                        title="Cluster spot-check failed — this photo was re-matched individually"
                      >
                        ⚠ split
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

